import React from 'react';
import { TemplateSummary, TemplatesResponse } from 'types/cx-editor';
import { MOCK_TEMPLATES_RESPONSE } from 'lib/asset-templates';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Box,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  alpha
} from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import {
  ChevronDown,
  PanelLeftClose,
  PanelRightOpen,
  Info,
  Mail,
  Globe,
  Newspaper,
  MessageSquare,
  Search
} from 'lucide-react';

type Props = {
  selectedTemplateKey?: string;
  onSelect: (templateKey: string) => void;
  isOpen?: boolean;
  onToggleOpen?: () => void;
}

const channelIconFor = (name: string) => {
  if (name === 'Email') return <Mail size={16} />;
  if (name === 'Web') return <Globe size={16} />;
  if (name === 'HER') return <Newspaper size={16} />;
  if (name === 'SMS') return <MessageSquare size={16} />;
  return null;
};

export const TemplatesSidebar: React.FC<Props> = ({ selectedTemplateKey, onSelect, isOpen = true, onToggleOpen }) => {
  // Using mock data for apple-to-apple comparison with studio version
  const data: TemplatesResponse = MOCK_TEMPLATES_RESPONSE;
  const loading = false;
  const error = null;

  return (
    <Card sx={{ display: 'flex', flexDirection: 'column', height: '100%', borderRadius: 0, width: '100%', minWidth: isOpen ? 300 : 48, overflow: 'hidden' }}>
      <CardHeader
        sx={{
          borderBottom: theme => `1px solid ${theme.palette.divider}`,
          py: 1.5,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 56
        }}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isOpen && (
              <>
                <Typography variant="h6" component="div" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                  CX Templates
                </Typography>
                <Tooltip title="These are the templates currently in scope for the project.">
                  <IconButton size="small" sx={{ p: 0.5 }}>
                    <Info size={16} style={{ color: 'inherit', opacity: 0.7 }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </div>
        }
        action={
          onToggleOpen ? (
            <Tooltip title={isOpen ? 'Collapse Templates bar' : 'Expand Templates bar'} placement={isOpen ? 'top' : 'right'}>
              <IconButton size="small" onClick={onToggleOpen} sx={{ ml: 'auto' }}>
                {isOpen ? <PanelLeftClose size={16} /> : <PanelRightOpen size={16} />}
              </IconButton>
            </Tooltip>
          ) : null
        }
      />
      <CardContent sx={{ pt: 1.5, px: 2, pb: 2, flex: 1, display: isOpen ? 'flex' : 'none', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
        <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
        {loading && (
          <Typography variant="body2" color="text.secondary">Loading…</Typography>
        )}
        {error && (
          <Typography variant="body2" color="error">{error}</Typography>
        )}

        <TextField
          placeholder="Search templates"
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} style={{ color: 'inherit', opacity: 0.7 }} />
              </InputAdornment>
            )
          }}
          sx={{ mb: 1.5 }}
        />

          {(data?.channels || []).map(ch => (
              <Accordion 
                key={ch.name}
                disableGutters 
                elevation={0} 
                defaultExpanded={ch.name === 'Email'}
                sx={{ 
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': { margin: 0 }
                }}
              >
                <AccordionSummary 
                  expandIcon={<ChevronDown size={16} />}
                  sx={{
                    minHeight: 40,
                    '&.Mui-expanded': { minHeight: 40 },
                    px: 1
                  }}
                >
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1, 
                      color: 'text.secondary', 
                      fontWeight: 600,
                      fontSize: '0.875rem'
                    }}
                  >
                    {channelIconFor(ch.name)}
                    {ch.name}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0.5, pb: 0.5, px: 1 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {(ch.templates || []).map((t: TemplateSummary) => {
                      const active = t.templateKey === selectedTemplateKey;
                      const match = t.name.match(/^\(([^)]+)\)\s*/);
                      const prefix = match ? match[1] : '';
                      const displayName = match ? t.name.replace(match[0], '').trim() : t.name;
                      return (
                        <Tooltip key={t.templateKey} title={t.templateKey} placement="right">
                          <Box
                            onClick={() => onSelect(t.templateKey)}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-start',
                              gap: 1,
                              width: '100%',
                              px: 1.5,
                              py: 1.5,
                              cursor: 'pointer',
                              borderRadius: 1,
                              textAlign: 'left',
                              border: active ? theme => `1px solid ${theme.palette.primary.main}` : '1px solid transparent',
                              backgroundColor: active 
                                ? theme => theme.palette.mode === 'dark' 
                                  ? alpha(theme.palette.primary.main, 0.16) 
                                  : alpha(theme.palette.primary.main, 0.08)
                                : 'transparent',
                              '&:hover': {
                                backgroundColor: theme => theme.palette.mode === 'dark' 
                                  ? alpha(theme.palette.common.white, 0.08) 
                                  : alpha(theme.palette.common.black, 0.04)
                              },
                              transition: 'all 0.2s ease-in-out'
                            }}
                          >
                            {prefix && (
                              <Chip 
                                size="small" 
                                variant="outlined" 
                                label={prefix}
                                sx={{
                                  height: 20,
                                  fontSize: '0.6875rem',
                                  fontWeight: 500,
                                  borderColor: theme => theme.palette.primary.main,
                                  color: theme => theme.palette.primary.main,
                                  backgroundColor: theme => theme.palette.primary.main + '1A',
                                  flexShrink: 0
                                }}
                              />
                            )}
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                overflow: 'hidden', 
                                textOverflow: 'ellipsis', 
                                whiteSpace: 'nowrap',
                                flex: 1,
                                fontSize: '0.875rem',
                                textAlign: 'left'
                              }}
                            >
                              {displayName}
                            </Typography>
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
        </div>
      </CardContent>
    </Card>
  );
}
